<script lang="ts">
  import {uid} from 'uid';
  import {cn} from 'Catalyst/cn';
  import {onMount, tick} from 'svelte';
  import BaseInput from 'Catalyst/components/BaseInput/BaseInput.svelte';
  import type {Plugin} from 'intl-tel-input';
  import 'intl-tel-input/build/js/utils.js';
  import {AsYouType, isValidPhoneNumber} from 'libphonenumber-js/max';
  import type {CountryCode} from 'libphonenumber-js';
  import {phoneInput} from 'Utility/phone/intlTelInput';
  import {formatNational} from 'Utility/formatters/phone';
  import type {PhoneInputProps} from '.';
  import {phoneInputTvProps} from '.';

  // eslint-disable-next-line no-unused-vars
  type $$Props = PhoneInputProps;

  export let id: PhoneInputProps['id'] = uid();
  export let label: PhoneInputProps['label'] = '';
  export let placeholder: PhoneInputProps['placeholder'] = '';
  export let value: PhoneInputProps['value'] = '';
  export let displayValue: PhoneInputProps['displayValue'] = '';
  export let size: PhoneInputProps['size'] =
    phoneInputTvProps.defaultVariants.size;
  export let disabled: PhoneInputProps['disabled'] =
    phoneInputTvProps.defaultVariants.disabled;
  export let invalid: PhoneInputProps['invalid'] = false;
  export let autocomplete: PhoneInputProps['autocomplete'] = 'tel';
  export let readonly: PhoneInputProps['readonly'] = false;
  export let initialCountry: PhoneInputProps['initialCountry'] = 'US';
  export let name: PhoneInputProps['name'] = 'phone';
  export let inputTestId: PhoneInputProps['inputTestId'] = `${id}-phone-input`;
  export let labelTestId: PhoneInputProps['labelTestId'] = 'phone-input-label';

  let hiddenInput: HTMLInputElement | null = null;
  let inputElement: HTMLInputElement | null = null;
  let iti: Plugin;

  const formatInput = (value: string) => {
    return new AsYouType(
      (iti.getSelectedCountryData().iso2?.toUpperCase() || 'US') as CountryCode
    ).input(value);
  };

  onMount(() => {
    if (inputElement) {
      iti = phoneInput(inputElement, {
        preferredCountries: ['us', 'ca', 'gb'],
        initialCountry: initialCountry,
        autoPlaceholder: 'off',
      });

      iti.setNumber(value || '');

      if (!value) return;

      // this parses & formats the first phone in a string (ie "4167388376 home" -> (416) 738-8376)
      displayValue = formatInput(value);
      iti.setNumber(displayValue);

      value = iti.getNumber();

      // force validation to run if mounted with a value that couldn't be parsed into a valid number
      if (!isValidPhoneNumber(value)) {
        tick().then(() => dispatchChange());
      } else {
        displayValue = formatNational(value, {nationalPrefix: false});
      }
    }
  });

  const updateValue = () => {
    displayValue = formatInput(inputElement.value);
    iti.setNumber(displayValue); // drops characters after phone #
    value = iti.getNumber(); // update the value of the hidden input field with E.164 format

    // trigger input event on hidden input with E.164 format for validation
    tick().then(() => dispatchChange());
  };

  const onBlurInput = () => {
    updateValue();
    hiddenInput?.dispatchEvent(new Event('blur'));
  };

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      updateValue();
    }
  };

  const dispatchChange = (): void => {
    hiddenInput?.dispatchEvent(new Event('change'));
  };

  $: klass = $$restProps.class;
  $: phoneInputTvSlots = phoneInputTvProps({
    size,
    disabled,
    floating: !!placeholder || !!displayValue || !!value,
    invalid,
    hidePlaceholder: !placeholder,
  });
  $: inputElement?.setCustomValidity(
    invalid ? 'Please enter a valid value.' : ''
  );
</script>

<div class={cn(phoneInputTvSlots.base(), klass)}>
  <BaseInput
    {...$$restProps}
    {id}
    class={phoneInputTvSlots.phoneInputWrapper()}
    {labelTestId}
    {label}
    {disabled}
    {invalid}
    value={displayValue}
    {placeholder}
  >
    <input
      {...$$restProps}
      bind:this={hiddenInput}
      id={name}
      {name}
      hidden
      {value}
      data-testid="input-{name}"
      {disabled}
    />
    <input
      {...$$restProps}
      {id}
      {name}
      bind:this={inputElement}
      class={phoneInputTvSlots.input()}
      {disabled}
      value={displayValue}
      data-testid={inputTestId}
      {autocomplete}
      {placeholder}
      {readonly}
      type="tel"
      on:change={updateValue}
      on:blur={onBlurInput}
      on:keydown={onKeydown}
      slot="input"
    />
  </BaseInput>
</div>
